import useToken from 'hooks/useToken';
import React, { useEffect, useState } from 'react';
import { Card, Container, Spinner, Row, Col, Table, DropdownButton, Dropdown } from 'react-bootstrap';
import { useParams, useNavigate } from 'react-router-dom';
import { useToast } from 'context/providers/ToastProvider';
import DatosFactura from '../../centro-xml/facturas-emitidas/visualizacion-factura/DatosFactura';
import SellosDigitales from '../../centro-xml/facturas-emitidas/visualizacion-factura/SellosDigitales';
import ConceptosFactura from '../../centro-xml/facturas-emitidas/visualizacion-factura/ConceptosFactura';
import BotonesAccion from '../../centro-xml/facturas-emitidas/visualizacion-factura/BotonesAccion';
import ConfirmationModal from '../../centro-xml/facturas-emitidas/lista-facturas/ConfirmationModal';
import ModalDownloadingDocument from 'akasia-components/Contabilidad/centro-xml/common/ModalDownloadingDocument';

const VisualizacionNomina = () => {
    const { showErrorToast, showSuccessToast } = useToast();
    const { token, setToken } = useToken();
    const { uuid } = useParams();
    const navigate = useNavigate();
    const [cargando, setCargando] = useState(false);
    const [receptor, setReceptor] = useState({});
    const [emisor, setEmisor] = useState({});
    const [facturaElectronica, setFacturaElectronica] = useState({});
    const [nominaDetails, setNominaDetails] = useState(null);
    const [datosSellos, setDatosSellos] = useState({});
    const [value, setValue] = useState('');
    const [conceptos, setConceptos] = useState([]);
    const [total, setTotal] = useState({});

    // Estados para modals y cancelación
    const [showModal, setShowModal] = useState(false);
    const [currentAction, setCurrentAction] = useState(null);
    const [isActive, setIsActive] = useState(true);

    const [isDownloading, setIsDownloading] = useState(false);
    const [downloadType, setDownloadType] = useState('generic');

    useEffect(() => {
        if (uuid) ObtenerXML();
    }, [uuid]);

    const formatNumberMX = (val) => {
        return new Intl.NumberFormat('es-MX', { style: 'decimal', minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(parseFloat(val || 0));
    };

    const ObtenerXML = async () => {
        setCargando(true);
        try {
            const response = await fetch(`/api/contabilidad/Nomina/ObtenerDatosVisualizacionNomina`, {
                method: 'POST',
                headers: {
                    'Authorization': 'Bearer ' + token,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({ uuid })
            });

            if (response.ok) {
                const data = await response.json();

                if (data.iResponseType === 1) {
                    const ObjDetalles = JSON.parse(data.detalles);
                    let vXMLString = ObjDetalles.table2[0].vXML;
                    let vXML;

                    try {
                        vXML = JSON.parse(vXMLString);
                    } catch (e) {
                        console.error("Error parsing JSON", e);
                        showErrorToast('El formato del comprobante no es válido.');
                        setCargando(false);
                        return;
                    }

                    setIsActive(Boolean(ObjDetalles.table2[0].bIsActive));

                    const comprobante = vXML.Comprobante || {};
                    const emisorNode = comprobante.Emisor || {};
                    const receptorNode = comprobante.Receptor || {};
                    const complemento = comprobante.Complemento || {};
                    const nominaNode = complemento.Nomina || {};

                    const emisorRegimen = ObjDetalles.table2[0].vRegimenFiscalEmisor;
                    const receptorUso = ObjDetalles.table2[0].vUsoCfdi;
                    const claveFormaPago = ObjDetalles.table2[0].vClave;
                    const regimenFiscal = ObjDetalles.table2[0].vRegimenFiscal;

                    setFacturaElectronica({
                        TipoDeComprobante: comprobante.TipoDeComprobante === "N" ? 'N - Nómina' : comprobante.TipoDeComprobante,
                        MetodoPago: comprobante.MetodoPago === "PUE" ? 'PUE - Pago en una sola exhibición' : (comprobante.MetodoPago === "PPD" ? 'PPD - Pago en parcialidades o diferido' : (comprobante.MetodoPago || 'N/A')),
                        FormaPago: claveFormaPago && claveFormaPago !== 'No especificado' ? claveFormaPago : (comprobante.FormaPago || 'N/A'),
                        Moneda: comprobante.Moneda,
                        LugarExpedicion: comprobante.LugarExpedicion,
                        Fecha: comprobante.Fecha,
                        Exportacion: comprobante.Exportacion === "01" ? '01 - No Aplica' : (comprobante.Exportacion || ''),
                        CertificadoEmisor: comprobante.NoCertificado,
                        Serie: comprobante.Serie,
                        Folio: comprobante.Folio,
                        TipoDeCambio: comprobante.TipoCambio,
                        CondicionesDePago: comprobante.CondicionesDePago,
                        NoCertificado: comprobante.NoCertificado
                    });

                    setEmisor({
                        ...emisorNode,
                        Nombre: emisorNode.Nombre,
                        Rfc: emisorNode.Rfc,
                        vRegimenFiscalEmisor: emisorRegimen && emisorRegimen !== 'No especificado' ? emisorRegimen : emisorNode.RegimenFiscal
                    });

                    setReceptor({
                        ...receptorNode,
                        Nombre: receptorNode.Nombre,
                        Rfc: receptorNode.Rfc,
                        vUsoCFDI: receptorUso && receptorUso !== 'No especificado' ? receptorUso : receptorNode.UsoCFDI,
                        vRegimen: regimenFiscal && regimenFiscal !== 'No especificado' ? regimenFiscal : receptorNode.RegimenFiscalReceptor,
                        NumRegIdTrib: receptorNode.NumRegIdTrib,
                        ResidenciaFiscal: receptorNode.ResidenciaFiscal,
                        DomicilioFiscal: receptorNode.DomicilioFiscalReceptor || receptorNode.DomicilioFiscal
                    });

                    let productos = comprobante.Conceptos?.Concepto;
                    if (productos) {
                        setConceptos(Array.isArray(productos) ? productos : [productos]);
                    }

                    let totalVal = comprobante.Total || 0;
                    let subtotal = comprobante.SubTotal || 0;
                    let descuento = comprobante.Descuento || null;

                    const totalFactura = {
                        dTotal: formatNumberMX(totalVal),
                        dTotalSinFormato: totalVal,
                        dSubtotal: formatNumberMX(subtotal),
                        dtrasladosImpuestos: null,
                        TasaOCuota: null,
                        Descuento: descuento > 0 ? formatNumberMX(descuento) : null,
                        Retenidos: null,
                        RetencionIVA: null,
                        RetencionISR: null,
                        RetencionIEPS: null,
                        TrasladoIVA: null,
                        TrasladoISR: null
                    };

                    setTotal(totalFactura);

                    console.log("Factura Electronica Mapeada: ", {
                        CertificadoEmisor: comprobante.NoCertificado,
                        DomicilioFiscal: receptorNode.DomicilioFiscalReceptor,
                        vRegimen: regimenFiscal,
                        Conceptos: productos
                    });

                    setNominaDetails({
                        FechaPago: nominaNode.FechaPago,
                        FechaInicialPago: nominaNode.FechaInicialPago,
                        FechaFinalPago: nominaNode.FechaFinalPago,
                        NumDiasPagados: nominaNode.NumDiasPagados,
                        TotalPercepciones: nominaNode.TotalPercepciones,
                        TotalDeducciones: nominaNode.TotalDeducciones,
                        TotalOtrosPagos: nominaNode.TotalOtrosPagos,
                        TipoNomina: nominaNode.TipoNomina,
                        Receptor: nominaNode.Receptor || {},
                        Emisor: nominaNode.Emisor || {},
                        Percepciones: nominaNode.Percepciones || {},
                        Deducciones: nominaNode.Deducciones || {},
                        OtrosPagos: nominaNode.OtrosPagos || {}
                    });

                    const tfd = complemento.TimbreFiscalDigital || complemento.timbreFiscalDigital || {};
                    if (tfd.UUID) {
                        const version = tfd.version || tfd.Version;
                        const cadenaOriginal = `||${version}|${tfd.UUID}|${tfd.FechaTimbrado}|${tfd.RfcProvCertif}|${tfd.SelloCFD}|${tfd.NoCertificadoSAT}||`;

                        setDatosSellos({
                            FechaTimbrado: tfd.FechaTimbrado,
                            NoCertificadoSAT: tfd.NoCertificadoSAT,
                            RfcProvCertif: tfd.RfcProvCertif,
                            SelloCFD: tfd.SelloCFD,
                            SelloSAT: tfd.SelloSAT,
                            UUID: tfd.UUID,
                            version: version,
                            certificacionSat: cadenaOriginal
                        });

                        let last8 = tfd.SelloCFD ? tfd.SelloCFD.slice(-8) : '';
                        setValue(`https://verificacfdi.facturaelectronica.sat.gob.mx/default.aspx?id=${tfd.UUID}&re=${emisorNode.Rfc}&rr=${receptorNode.Rfc}&tt=${totalVal}&fe=${last8}`);
                    }

                    setCargando(false);
                } else {
                    showErrorToast(data.vMessage || 'No se pudo obtener el XML');
                    setCargando(false);
                }
            } else {
                showErrorToast(response.status + ' ' + response.statusText);
                if (response.status === 401) {
                    setToken(null);
                    window.location.href = "";
                }
                setCargando(false);
            }
        } catch (error) {
            console.error('Error al obtener datos de la nómina:', error);
            showErrorToast('Error al obtener los datos.');
            setCargando(false);
        }
    };

    /**
    * Funciones para Modal y Acciones
    */
    const handleOpenModal = (action) => {
        setCurrentAction(action);
        setShowModal(true);
    };

    const handleCloseModal = () => {
        setShowModal(false);
        setCurrentAction(null);
    };

    const handleConfirmAction = async () => {
        try {
            if (currentAction === 'eliminar') {
                await eliminarFactura();
                setShowModal(false);
            } else if (currentAction === 'cancelar') {
                await cancelarFactura();
                setShowModal(false);
            } else if (currentAction === 'cancelarSAT') {
                setShowModal(false);
            }
        } catch (error) {
            console.error(`Error al ${currentAction} la nómina:`, error);
        }
    };

    const eliminarFactura = async () => {
        setCargando(true);
        const response = await fetch("/api/contabilidad/Nomina/EliminaNomina", {
            method: 'POST',
            headers: {
                'Authorization': 'Bearer ' + token,
                'Content-Type': 'application/json'
            },
            body: JSON.stringify({ uuid })
        }).catch((error) => {
            setCargando(false);
            showErrorToast('Hubo un problema con la peticion Fetch:' + error);
        });

        if (response && response.ok) {
            setCargando(false);
            const data = await response.json();
            if (data.iResponseType === 1) {
                showSuccessToast(data.vMessage);
                navigate("/contabilidad/nomina/timbrado");
            } else {
                showErrorToast(data.vMessage);
            }
        } else if (response) {
            setCargando(false);
            showErrorToast(response.status + ' ' + response.statusText);
        }
    };

    const cancelarFactura = async () => {
        setCargando(true);
        const response = await fetch("/api/contabilidad/Nomina/CancelaNomina", {
            method: 'POST',
            headers: {
                'Authorization': 'Bearer ' + token,
                'Content-Type': 'application/json'
            },
            body: JSON.stringify({ uuid })
        }).catch((error) => {
            setCargando(false);
            showErrorToast('Hubo un problema con la peticion Fetch:' + error);
        });

        if (response && response.ok) {
            setCargando(false);
            const data = await response.json();
            if (data.iResponseType === 1) {
                showSuccessToast(data.vMessage);
                setIsActive(false);
            } else {
                showErrorToast(data.vMessage);
            }
        } else if (response) {
            setCargando(false);
            showErrorToast(response.status + ' ' + response.statusText);
        }
    };

    const refreshFactura = async () => {
        await ObtenerXML();
    };

    const formatCurrency = (amount) => {
        const val = parseFloat(amount || 0);
        return new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN' }).format(val);
    };

    const descargarXML = async () => {
        setIsDownloading(true);
        setDownloadType('xml');
        try {
            const aniomes = facturaElectronica.Fecha ? (facturaElectronica.Fecha.substring(0, 4) + facturaElectronica.Fecha.substring(5, 7)) : '';
            const rfc = emisor?.Rfc || '';
            const response = await fetch(`/api/contabilidad/Nomina/DescargarXMLNomina?uuid=${uuid}&aniomes=${aniomes}&rfc=${rfc}`, {
                method: 'GET',
                headers: {
                    'Authorization': 'Bearer ' + token
                }
            });
            if (response.ok) {
                const blob = await response.blob();
                const url = window.URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url;
                a.download = `Nomina_${uuid}.xml`;
                document.body.appendChild(a);
                a.click();
                a.remove();
            } else {
                try {
                    const data = await response.json();
                    if (data && data.vMessage) {
                        showErrorToast(data.vMessage);
                    } else {
                        showErrorToast('Error al descargar el XML');
                    }
                } catch (e) {
                    showErrorToast('Error al descargar el XML');
                }
            }
        } catch (error) {
            console.error('Error al descargar XML:', error);
            showErrorToast('Error al descargar el XML');
        } finally {
            setIsDownloading(false);
        }
    };

    const descargarPDF = async () => {
        setIsDownloading(true);
        setDownloadType('pdf');
        try {
            const response = await fetch(`/api/contabilidad/Nomina/DescargarPDFNomina?uuid=${uuid}`, {
                method: 'GET',
                headers: {
                    'Authorization': 'Bearer ' + token
                }
            });
            if (response.ok) {
                const blob = await response.blob();
                const url = window.URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url;
                a.download = `Nomina_${uuid}.pdf`;
                document.body.appendChild(a);
                a.click();
                a.remove();
            } else {
                try {
                    const data = await response.json();
                    if (data && data.vMessage) {
                        showErrorToast(data.vMessage);
                    } else {
                        showErrorToast('El PDF aún no está disponible o hubo un error.');
                    }
                } catch (e) {
                    showErrorToast('El PDF aún no está disponible o hubo un error.');
                }
            }
        } catch (error) {
            console.error('Error al descargar PDF:', error);
            showErrorToast('Error al descargar el PDF');
        } finally {
            setIsDownloading(false);
        }
    };

    return (
        <Card className="border-0 shadow-sm">
            <Container fluid className="py-4 px-4">
                <div className="d-flex justify-content-between align-items-center mb-4 border-bottom pb-2">
                    <div className="d-flex align-items-center">
                        <h4 className="mb-0 text-dark me-3">Visualización de Nómina</h4>
                    </div>
                    {!cargando && isActive !== null && (
                        <div className="d-flex align-items-center">
                            <span
                                className="me-2"
                                style={{
                                    width: '10px',
                                    height: '10px',
                                    borderRadius: '50%',
                                    backgroundColor: isActive ? 'green' : 'red',
                                    display: 'inline-block'
                                }}
                            ></span>
                            {isActive ? (
                                <DropdownButton
                                    variant="success"
                                    title="Nómina Activa"
                                    size="sm"
                                >
                                    <Dropdown.Item onClick={descargarPDF}>Descargar PDF</Dropdown.Item>
                                    <Dropdown.Item onClick={descargarXML}>Descargar XML</Dropdown.Item>
                                </DropdownButton>
                            ) : (
                                <span className="text-danger fw-bold">Nómina Cancelada</span>
                            )}
                        </div>
                    )}
                </div>

                {cargando ? (
                    <div className="text-center py-5">
                        <Spinner animation="border" variant="secondary" />
                        <h6 className="mt-3 text-muted">Cargando XML...</h6>
                    </div>
                ) : (
                    <>

                        <DatosFactura
                            emisor={emisor}
                            receptor={receptor}
                            facturaElectronica={facturaElectronica}
                        />

                        {conceptos && conceptos.length > 0 && (
                            <ConceptosFactura conceptos={conceptos} total={total} facturaElectronica={facturaElectronica} />
                        )}

                        {nominaDetails && (
                            <div className="mt-4">
                                <h5 className="mb-3 text-dark border-bottom pb-2">Datos Generales</h5>
                                <Table bordered size="sm" className="mb-4 bg-white" style={{ fontSize: '0.85rem' }}>
                                    <tbody>
                                        <tr>
                                            <th className="bg-light" style={{ width: '25%' }}>Tipo Nómina</th>
                                            <td style={{ width: '25%' }}>{nominaDetails.TipoNomina === 'O' ? 'Ordinaria' : nominaDetails.TipoNomina === 'E' ? 'Extraordinaria' : nominaDetails.TipoNomina}</td>
                                            <th className="bg-light" style={{ width: '25%' }}>Fecha de Pago</th>
                                            <td style={{ width: '25%' }}>{nominaDetails.FechaPago}</td>
                                        </tr>
                                        <tr>
                                            <th className="bg-light">Periodo</th>
                                            <td>{nominaDetails.FechaInicialPago} al {nominaDetails.FechaFinalPago}</td>
                                            <th className="bg-light">Días Pagados</th>
                                            <td>{nominaDetails.NumDiasPagados}</td>
                                        </tr>
                                    </tbody>
                                </Table>

                                <h5 className="mb-3 text-dark border-bottom pb-2">Datos del Empleado</h5>
                                <Table bordered size="sm" className="mb-4 bg-white" style={{ fontSize: '0.85rem' }}>
                                    <tbody>
                                        <tr>
                                            <th className="bg-light" style={{ width: '25%' }}>Num Empleado</th>
                                            <td style={{ width: '25%' }}>{nominaDetails.Receptor.NumEmpleado || 'N/A'}</td>
                                            <th className="bg-light" style={{ width: '25%' }}>CURP</th>
                                            <td style={{ width: '25%' }}>{nominaDetails.Receptor.Curp || 'N/A'}</td>
                                        </tr>
                                        <tr>
                                            <th className="bg-light">NSS</th>
                                            <td>{nominaDetails.Receptor.NumSeguridadSocial || 'N/A'}</td>
                                            <th className="bg-light">Registro Patronal</th>
                                            <td>{nominaDetails.Emisor.RegistroPatronal || 'N/A'}</td>
                                        </tr>
                                        <tr>
                                            <th className="bg-light">Departamento</th>
                                            <td>{nominaDetails.Receptor.Departamento || 'N/A'}</td>
                                            <th className="bg-light">Puesto</th>
                                            <td>{nominaDetails.Receptor.Puesto || 'N/A'}</td>
                                        </tr>
                                        <tr>
                                            <th className="bg-light">Tipo Contrato</th>
                                            <td>{nominaDetails.Receptor.TipoContrato || 'N/A'}</td>
                                            <th className="bg-light">Tipo Jornada</th>
                                            <td>{nominaDetails.Receptor.TipoJornada || 'N/A'}</td>
                                        </tr>
                                        <tr>
                                            <th className="bg-light">Inicio Rel. Laboral</th>
                                            <td>{nominaDetails.Receptor.FechaInicioRelLaboral || 'N/A'}</td>
                                            <th className="bg-light">Antigüedad</th>
                                            <td>{nominaDetails.Receptor.Antigüedad || nominaDetails.Receptor.Antiguedad || 'N/A'}</td>
                                        </tr>
                                        <tr>
                                            <th className="bg-light">Salario Diario Int.</th>
                                            <td>{nominaDetails.Receptor.SalarioDiarioIntegrado ? formatCurrency(nominaDetails.Receptor.SalarioDiarioIntegrado) : 'N/A'}</td>
                                            <th className="bg-light">SBC</th>
                                            <td>{nominaDetails.Receptor.SalarioBaseCotApor ? formatCurrency(nominaDetails.Receptor.SalarioBaseCotApor) : 'N/A'}</td>
                                        </tr>
                                        <tr>
                                            <th className="bg-light">Banco</th>
                                            <td>{nominaDetails.Receptor.Banco || 'N/A'}</td>
                                            <th className="bg-light">Cuenta Bancaria</th>
                                            <td>{nominaDetails.Receptor.CuentaBancaria || 'N/A'}</td>
                                        </tr>
                                        <tr>
                                            <th className="bg-light">Periodicidad Pago</th>
                                            <td>{nominaDetails.Receptor.PeriodicidadPago || 'N/A'}</td>
                                            <th className="bg-light">Sindicalizado</th>
                                            <td>{nominaDetails.Receptor.Sindicalizado || 'N/A'}</td>
                                        </tr>
                                    </tbody>
                                </Table>

                                <Row>
                                    <Col lg={12}>
                                        <h5 className="mb-2 text-dark">Percepciones</h5>
                                        <Table bordered size="sm" className="bg-white mb-4" style={{ fontSize: '0.85rem' }}>
                                            <thead className="bg-light">
                                                <tr>
                                                    <th>Tipo</th>
                                                    <th>Clave</th>
                                                    <th>Concepto</th>
                                                    <th className="text-end">Importe Gravado</th>
                                                    <th className="text-end">Importe Exento</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {nominaDetails.Percepciones?.Percepcion?.length > 0 ? (
                                                    nominaDetails.Percepciones.Percepcion.map((p, i) => (
                                                        <tr key={i}>
                                                            <td>{p.TipoPercepcion}</td>
                                                            <td>{p.Clave}</td>
                                                            <td>{p.Concepto}</td>
                                                            <td className="text-end">{formatCurrency(p.ImporteGravado)}</td>
                                                            <td className="text-end">{formatCurrency(p.ImporteExento)}</td>
                                                        </tr>
                                                    ))
                                                ) : (
                                                    <tr><td colSpan="5" className="text-center text-muted">Sin percepciones</td></tr>
                                                )}
                                            </tbody>
                                            <tfoot>
                                                <tr>
                                                    <td colSpan="3" className="text-end fw-bold">Total Percepciones:</td>
                                                    <td colSpan="2" className="text-end fw-bold">{formatCurrency(nominaDetails.Percepciones?.TotalSueldos || nominaDetails.TotalPercepciones)}</td>
                                                </tr>
                                            </tfoot>
                                        </Table>
                                    </Col>
                                </Row>

                                <Row>
                                    <Col lg={6}>
                                        <h5 className="mb-2 text-dark">Deducciones</h5>
                                        <Table bordered size="sm" className="bg-white mb-4" style={{ fontSize: '0.85rem' }}>
                                            <thead className="bg-light">
                                                <tr>
                                                    <th>Tipo</th>
                                                    <th>Clave</th>
                                                    <th>Concepto</th>
                                                    <th className="text-end">Importe</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {nominaDetails.Deducciones?.Deduccion?.length > 0 ? (
                                                    nominaDetails.Deducciones.Deduccion.map((d, i) => (
                                                        <tr key={i}>
                                                            <td>{d.TipoDeduccion}</td>
                                                            <td>{d.Clave}</td>
                                                            <td>{d.Concepto}</td>
                                                            <td className="text-end">{formatCurrency(d.Importe)}</td>
                                                        </tr>
                                                    ))
                                                ) : (
                                                    <tr><td colSpan="4" className="text-center text-muted">Sin deducciones</td></tr>
                                                )}
                                            </tbody>
                                            <tfoot>
                                                <tr>
                                                    <td colSpan="3" className="text-end fw-bold">Total Deducciones:</td>
                                                    <td className="text-end fw-bold">{formatCurrency(nominaDetails.TotalDeducciones)}</td>
                                                </tr>
                                            </tfoot>
                                        </Table>
                                    </Col>

                                    <Col lg={6}>
                                        <h5 className="mb-2 text-dark">Otros Pagos</h5>
                                        <Table bordered size="sm" className="bg-white mb-4" style={{ fontSize: '0.85rem' }}>
                                            <thead className="bg-light">
                                                <tr>
                                                    <th>Tipo</th>
                                                    <th>Clave</th>
                                                    <th>Concepto</th>
                                                    <th className="text-end">Importe</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {nominaDetails.OtrosPagos?.OtroPago?.length > 0 ? (
                                                    nominaDetails.OtrosPagos.OtroPago.map((o, i) => (
                                                        <tr key={i}>
                                                            <td>{o.TipoOtroPago}</td>
                                                            <td>{o.Clave}</td>
                                                            <td>{o.Concepto}</td>
                                                            <td className="text-end">{formatCurrency(o.Importe)}</td>
                                                        </tr>
                                                    ))
                                                ) : (
                                                    <tr><td colSpan="4" className="text-center text-muted">Sin otros pagos</td></tr>
                                                )}
                                            </tbody>
                                            <tfoot>
                                                <tr>
                                                    <td colSpan="3" className="text-end fw-bold">Total Otros Pagos:</td>
                                                    <td className="text-end fw-bold">{formatCurrency(nominaDetails.TotalOtrosPagos)}</td>
                                                </tr>
                                            </tfoot>
                                        </Table>
                                    </Col>
                                </Row>

                                <Row className="mt-2 mb-4">
                                    <Col lg={12} className="d-flex justify-content-end">
                                        <div className="border p-2 bg-light" style={{ minWidth: '250px' }}>
                                            <div className="d-flex justify-content-between align-items-center">
                                                <span className="fw-bold text-dark" style={{ fontSize: '1.05rem' }}>Neto a Pagar:</span>
                                                <span className="fw-bold text-dark" style={{ fontSize: '1.05rem' }}>
                                                    {formatCurrency(
                                                        (parseFloat(nominaDetails.TotalPercepciones || 0) +
                                                            parseFloat(nominaDetails.TotalOtrosPagos || 0) -
                                                            parseFloat(nominaDetails.TotalDeducciones || 0))
                                                    )}
                                                </span>
                                            </div>
                                        </div>
                                    </Col>
                                </Row>

                                {datosSellos.UUID && (
                                    <div className="mt-4 pt-4 border-top">
                                        <SellosDigitales datosSellos={datosSellos} value={value} />
                                    </div>
                                )}

                                <BotonesAccion
                                    isActive={isActive}
                                    handleOpenModal={handleOpenModal}
                                    datosSellos={datosSellos}
                                    emisor={emisor}
                                    receptor={receptor}
                                    total={total?.dTotalSinFormato}
                                    refreshFactura={refreshFactura}
                                />
                            </div>
                        )}
                    </>
                )}

                <ConfirmationModal
                    show={showModal}
                    onHide={handleCloseModal}
                    onConfirm={handleConfirmAction}
                    action={currentAction}
                    IsActive={isActive}
                    message={`¿Estás seguro de que deseas ${currentAction} esta nómina?`}
                    title={`Confirmar ${currentAction === 'eliminar' ? 'eliminación' : 'cancelación'}`}
                />
            </Container>
            {/* Componente ModalDownloadingDocument */}
            <ModalDownloadingDocument
                show={isDownloading}
                documentType={downloadType}
                title={downloadType === 'pdf' ? 'Generando PDF' : 'Descargando XML'}
            />
        </Card>
    );
};

export default VisualizacionNomina;
